import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useRouteContext, useRouter } from '@tanstack/react-router';
import { Button, Flex, Form, Input, message, Result, Typography } from 'antd';
import { isAxiosError } from 'axios';
import { ArrowLeft, RotateCw } from 'lucide-react';
import { useEffect } from 'react';

import {
  getGetAccountQueryKey,
  getGetOrganizationQueryKey,
  useGetOrganization,
  useUpdateOrganizationProfile,
} from '../../../../api';
import { usePermissions } from '../../../../modules/account/hooks';
import { AvatarReshuffle } from '../../../../modules/avatar/components/avatar-reshuffle';
import {
  OrganizationProfileFormFields,
  OrganizationProfileFormFieldValues,
} from '../../../../modules/organization/components/organization-profile-form';
import { SettingsLayoutContent } from '../../../../ui/components/layout';
import { Loader } from '../../../../ui/components/loader';
import { useUnsavedChangesGuard } from '../../../../ui/hooks';
import { ensureUrlHasProtocol, formatUrlForDisplay } from '../../../../ui/utils/url';

const SettingsCompanyRoute = () => {
  const queryClient = useQueryClient();
  const router = useRouter();

  const { selectedOrganization } = useRouteContext({ from: '/_authenticated/_app' });
  const { can } = usePermissions();
  const organizationId = selectedOrganization.id;
  const canEdit = can('settings', 'manageProfile');

  const { data: organizationResponse, error, status, refetch } = useGetOrganization(organizationId);
  const organization = organizationResponse?.data;

  const [form] = Form.useForm<OrganizationProfileFormFieldValues & { avatarIndex?: number }>();
  Form.useWatch([], form);
  useUnsavedChangesGuard(() => form.isFieldsTouched());

  useEffect(() => {
    form.resetFields();
  }, [form, organization]);

  const { mutate: updateProfile, isPending } = useUpdateOrganizationProfile({
    mutation: {
      onSuccess: () => {
        message.success('Saved');
        queryClient.invalidateQueries({
          queryKey: getGetOrganizationQueryKey(organizationId),
        });

        // Must "reset" because ensureQuery in _app.tsx will not properly catch up with new data
        queryClient.resetQueries({ queryKey: getGetAccountQueryKey() });
        router.invalidate();
      },
      onError: () => {
        message.error('Failed to save');
      },
    },
  });

  const handleFinish = (values: OrganizationProfileFormFieldValues & { avatarIndex?: number }) => {
    updateProfile({
      organizationId,
      data: {
        name: values.name,
        address: values.address || null,
        website: values.website ? ensureUrlHasProtocol(values.website) : null,
        timezone: values.timezone,
        ...(form.isFieldTouched('avatarIndex') ? { avatarIndex: values.avatarIndex } : {}),
      },
    });
  };

  const content = {
    pending: <Loader />,
    error:
      isAxiosError(error) && error.response?.status === 403 ? (
        <Result
          status="403"
          title="Access Denied"
          subTitle="You do not have permission to view or edit this page. Please contact your administrator."
          extra={
            <Button icon={<ArrowLeft size="1em" />} onClick={() => window.history.back()}>
              Go back
            </Button>
          }
        />
      ) : (
        <Result
          status="error"
          title="Failed to load!"
          subTitle="Please try again. If this keeps happening, check your connection or contact support."
          extra={
            <Button icon={<RotateCw size="1em" />} onClick={() => refetch()}>
              Try again
            </Button>
          }
        />
      ),
    success: (
      <>
        <Flex vertical gap={8} style={{ marginBottom: 24 }}>
          <Typography.Text style={{ fontSize: 14, lineHeight: '22px' }}>
            Company Avatar
          </Typography.Text>
          <Flex align="center" gap={16}>
            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.avatarIndex !== curr.avatarIndex}>
              {({ getFieldValue }) => (
                <AvatarReshuffle
                  kind="organization"
                  id={organizationId}
                  currentIndex={getFieldValue('avatarIndex') ?? organization?.avatarIndex}
                  name={organization?.name ?? ''}
                  size={80}
                  onReshuffle={(nextIndex) => {
                    form.setFields([{ name: 'avatarIndex', value: nextIndex, touched: true }]);
                  }}
                  disabled={!canEdit || isPending}
                />
              )}
            </Form.Item>
            <Flex vertical>
              <Typography.Text style={{ fontWeight: 500, fontSize: 13 }}>
                Company Avatar
              </Typography.Text>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Click the avatar to shuffle options.
              </Typography.Text>
            </Flex>
          </Flex>
        </Flex>
        <Form.Item name="avatarIndex" hidden>
          <Input />
        </Form.Item>
        <OrganizationProfileFormFields showLabels />
      </>
    ),
  };

  return (
    <>
      {status === 'success' ? (
        <Form
          form={form}
          layout="vertical"
          validateTrigger={['onSubmit', 'onChange']}
          disabled={!canEdit}
          onFinish={handleFinish}
          initialValues={{
            ...organization,
            website: organization?.website ? formatUrlForDisplay(organization.website) : '',
          }}
        >
          <SettingsLayoutContent
            title="Company"
            description="Company details and branding."
            footer={
              canEdit ? (
                <Form.Item shouldUpdate noStyle>
                  {({ isFieldsTouched }) => {
                    const isDirty = isFieldsTouched();
                    return (
                      <Flex justify={isDirty ? 'space-between' : 'flex-end'}>
                        {isDirty && (
                          <Button onClick={() => form.resetFields()} disabled={isPending}>
                            Reset
                          </Button>
                        )}
                        <Button
                          type="primary"
                          htmlType="submit"
                          loading={isPending}
                          disabled={!isDirty && !isPending}
                        >
                          Save
                        </Button>
                      </Flex>
                    );
                  }}
                </Form.Item>
              ) : null
            }
          >
            {content.success}
          </SettingsLayoutContent>
        </Form>
      ) : (
        <SettingsLayoutContent title="Company" description="Company details and branding.">
          {status === 'pending' ? content.pending : content.error}
        </SettingsLayoutContent>
      )}
    </>
  );
};

export const Route = createFileRoute('/_authenticated/_app/settings/company')({
  component: SettingsCompanyRoute,
});
